import { Flex } from 'antd';
import {
  useGetAssetsQuery,
  useGetProjectMapQuery,
  usePutAssetMutation,
  useGetSurveysQuery,
  usePostSurveyMutation,
  usePostSurveyMemberBatchMutation,
  useMergeSurveysMutation,
  usePostAssetEdgeMutation,
} from '../../features/api/apiSlice';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import * as maptilersdk from '@maptiler/sdk';
import '@maptiler/sdk/dist/maptiler-sdk.css';
import useIsMobile from '../../utils/mediaQuery';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { message } from 'antd';
import { calculateBounds } from '../../utils/calculateZoomLevel';
import './ProjectMap.css';
import AssetView from '../assets/asset-view/AssetView';
import { createPhotoObject } from '../../utils/photoUtilities';
import { v4 as uuidv4 } from 'uuid';
import { OverlayPanel } from 'primereact/overlaypanel';
import { MapStyleControl } from './MapStyleControl';

export default function ProjectMap({ drawingsSelected, active }) {
  const ASSET_COLOR = '#1677ff';
  const LAST_MODIFIED_COLOR = '#3dabf0';
  const HIGHLIGHTED_COLOR = '#ff4d4f';
  const FLASH_COLOR = 'red';
  const CURRENT_SCREEN = 'MAP';

  // Hooks and API calls
  const location = useLocation();
  const urlSearch = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [searchedSurveyId, setSearchedSurveyId] = useState(null);

  const { projectId } = useParams();
  const { data: assetsFromApi, isLoading: assetsLoading } = useGetAssetsQuery(projectId);
  const [updateAsset] = usePutAssetMutation();
  const { data: project, isLoading: projectLoading } = useGetProjectMapQuery(projectId);
  const isMobile = useIsMobile();

  const {
    data: surveysFromApi = [],
    isLoading: surveysLoading = false,
    refetch: refetchSurveys,
  } = useGetSurveysQuery({ projectId, includeMembers: true, includeEdges: true });
  const [createSurvey] = usePostSurveyMutation();
  const [createSurveyMemberBatch] = usePostSurveyMemberBatchMutation();
  const [mergeSurveys] = useMergeSurveysMutation();
  const [postAssetEdge] = usePostAssetEdgeMutation();

  // State
  const [surveys, setSurveys] = useState([]);

  const [assets, setAssets] = useState([]);
  const [visibleAssets, setVisibleAssets] = useState([]);
  const [allLayers, setAllLayers] = useState([]);
  const [visibleLayers, setVisibleLayers] = useState([]);
  const [distributionPointsMarkers, setDistributionPointsMarkers] = useState([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [assetsToConnect, setAssetsToConnect] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [lastModifiedAsset, setLastModifiedAsset] = useState(null);
  const [assetsOptions, setAssetsOptions] = useState([]);
  const [selectedLayers, setSelectedLayers] = useState([]);
  const [zoom] = useState(14);
  const [isAssetViewOpen, setIsAssetViewOpen] = useState(false);
  const [mapStyle, setMapStyle] = useState('satellite');
  const assetViewOpen = useRef(false);
  const [assetViewAsset, setAssetViewAsset] = useState(null);
  const mapStyleOverlayRef = useRef(null);
  const mapStyleControlRef = useRef(null);
  const mapStyleControlButtonRef = useRef(null);

  const [ppkPosition, setPpkPosition] = useState(null);
  const [ppkLoading, setPpkLoading] = useState(false);
  const [locationType, setLocationType] = useState('PPK');

  // Refs
  const mapContainer = useRef(null);
  const map = useRef(null);
  const skipMarkerRefresh = useRef(false);
  const assetMarkers = useRef([]);
  const menuRef = useRef(null);
  const linkingAssetsRef = useRef(false);
  const loadAssetsRef = useRef(null);
  const loadSurveysLinesRef = useRef(null);

  const [messageApi, contextHolder] = message.useMessage();

  const drawingsSelectedRef = useRef(drawingsSelected);

  const clearMobilePhotos = () => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'CLEAR-PHOTOS', screen: CURRENT_SCREEN })
    );
  };

  const openCamera = () =>
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'TRIGGER-CAMERA', screen: CURRENT_SCREEN })
    );

  const requestPPKPosition = () => {
    setPpkLoading(true);
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'REQUEST-PPK', screen: CURRENT_SCREEN })
    );
  };

  const requestGeolocation = (omitVertical = false, omitHorizontal = false) => {
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({
        type: 'REQUEST-GEOLOCATION',
        screen: CURRENT_SCREEN,
        omitVertical,
        omitHorizontal,
      })
    );
  };

  const [locationTimedOut, setLocationTimedOut] = useState(false);

  const handleWebViewMessage = async (event) => {
    let msg = event.data;
    if (typeof msg === 'string') {
      const trimmed = msg.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          msg = JSON.parse(trimmed);
        } catch (e) {
          throw new Error('Invalid message from WebView, could not parse JSON: ' + trimmed, e);
        }
      } else return;
    }

    switch (msg.type) {
      case 'PPK_POSITION_TIMED_OUT':
        setLocationTimedOut(msg.value);
        break;
      case 'PPK_POSITION_UPDATE_START':
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: 'LOG', content: '-------------------------------------' })
        );
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: 'LOG', content: 'PPK_POSITION_UPDATE_START' })
        );
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOG', content: msg }));
        if (msg.screen !== CURRENT_SCREEN) return;
        displayMessage('PPK position update started', 'info');
        setPpkLoading(true);
        break;
      case 'PPK_POSITION_UPDATE':
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'LOG', content: '-------------------------------------' })
        );
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: 'LOG', content: 'PPK position update' })
        );
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOG', content: msg }));
        if (msg.screen !== CURRENT_SCREEN) return;

        if (msg.error) {
          displayMessage(msg.message, 'error');
          requestGeolocation();
        } else {
          const { vertical, horizontal } = msg?.position?.accuracy;
          window.ReactNativeWebView?.postMessage(
            JSON.stringify({
              type: 'LOG',
              content: 'PPK accuracy: ' + vertical + ' ' + horizontal,
            })
          );
          if (vertical > 5 || horizontal > 5) {
            displayMessage(
              'PPK position accuracy is too low. Falling back to geolocation.',
              'warning'
            );
            setPpkPosition(msg.position);
            setLocationType({
              vertical: msg.locationType,
              horizontal: msg.locationType,
              altitude: msg.locationType,
            });
            requestGeolocation(vertical < 5, horizontal < 5);
            setPpkLoading(false);
            return;
          }
          setPpkPosition(msg.position);
          setLocationType({
            vertical: msg.locationType,
            horizontal: msg.locationType,
            altitude: msg.locationType,
          });
        }
        setPpkLoading(false);
        break;
      case 'GEOLOCATION_UPDATE':
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: 'LOG', content: '-------------------------------------' })
        );
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: 'LOG', content: 'GEOLOCATION_UPDATE' })
        );
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOG', content: msg }));
        if (msg.screen !== CURRENT_SCREEN) return;
        if (msg.error) {
          displayMessage(msg.message, 'error');
          requestPPKPosition();
        } else {
          const omittedVertical = msg.omittedVertical;
          const omittedHorizontal = msg.omittedHorizontal;

          if (omittedHorizontal || omittedVertical) {
            const prev = ppkPosition;

            if (!omittedVertical) {
              prev.latitude = msg.position.latitude;
            }

            if (!omittedHorizontal) {
              prev.longitude = msg.position.longitude;
            }

            setPpkPosition(prev);
            setLocationType({
              vertical: omittedVertical ? 'Geo' : 'PPK',
              horizontal: omittedHorizontal ? 'Geo' : 'PPK',
              altitude: 'Geo',
            });
            return;
          }
          setLocationType({
            vertical: msg.locationType,
            horizontal: msg.locationType,
            altitude: msg.locationType,
          });
          setPpkPosition(msg.position);
        }
        break;
      case 'PHOTOS_UPDATE': {
        if (msg.screen !== CURRENT_SCREEN) return;
        window.ReactNativeWebView?.postMessage(
          JSON.stringify({ type: 'LOG', content: 'Asset view open:' + assetViewOpen.current })
        );
        const data = msg;
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOG', content: data }));

        const standardizedPhotos = msg.photos.map((photo) => {
          // Create the photo object for storage
          return {
            ...createPhotoObject({ ...photo, isExisting: false }),
            base64: photo.base64 || null,
          };
        });

        const assetForAssetView = {
          id: -1,
          photos: standardizedPhotos,
          project_id: projectId,
          isNew: true,
        };

        setAssetViewAsset(assetForAssetView);
        setIsAssetViewOpen(true);
        assetViewOpen.current = true;

        clearMobilePhotos();
        break;
      }
      case 'CAMERA_CLOSED': {
        setIsAssetViewOpen(false);
        assetViewOpen.current = false;
        break;
      }
      default:
        console.log('Unhandled:', msg.type);
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleWebViewMessage);
    document.addEventListener('message', handleWebViewMessage);
    return () => {
      window.removeEventListener('message', handleWebViewMessage);
      document.removeEventListener('message', handleWebViewMessage);
    };
  }, []);

  useEffect(() => {
    drawingsSelectedRef.current = drawingsSelected;
  }, [drawingsSelected]);

  useEffect(() => {
    for (const [key, value] of urlSearch.entries()) {
      if (key === 'surveyId' && value) {
        setSearchedSurveyId(value);
      }
    }

    return () => {
      setSearchedSurveyId(null);
    };
  }, [urlSearch]);

  useEffect(() => {
    if (!map.current || !searchedSurveyId || !surveysFromApi) return;

    const members = surveysFromApi.find((s) => s.id === Number(searchedSurveyId))?.members;

    if (!members || members.length === 0) return;

    // Extract coordinates from members
    const coordsList = members.map((m) => [m.longitude, m.latitude]);

    const assetIds = members.map((m) => m.asset_id);
    const assetsToHighlight = visibleAssets.filter((asset) => assetIds.includes(asset.id));

    // Highlight assets in this survey
    setTimeout(() => {
      const bounds = calculateBounds(coordsList);

      if (bounds) {
        map.current.fitBounds(bounds, {
          padding: { top: 100, bottom: 100, left: 100, right: 100 },
          maxZoom: 50,
          duration: 1500,
        });
      }

      assetsToHighlight.forEach((asset) => {
        colorAsset(asset, HIGHLIGHTED_COLOR);
      });
    }, 500);

    return () => {
      assetsToHighlight.forEach((asset) => {
        resetMarker(asset);
        if (asset.id === lastModifiedAsset?.id) {
          colorAsset(asset, LAST_MODIFIED_COLOR);
        } else {
          colorAsset(asset, ASSET_COLOR);
        }
      });
    };
  }, [searchedSurveyId, map.current, visibleAssets]);

  const MAP_STYLES_OPTIONS = {
    Street: 'nl-cartiqo-topo',
    Satellite: 'satellite',
  };

  const layersOptions = useMemo(() => {
    if (!project || !project.drawings) return [];

    return project.drawings.map((drawing) => ({
      key: drawing.key,
      id: drawing.id,
      label: drawing.name,
      color: drawing.color,
      type: drawing.type,
    }));
  }, [project]);

  // Map configuration
  maptilersdk.config.apiKey = 'Ha0wpPicSoh3DDh4limA';

  const centerSetRef = useRef(false);

  const initMap = useCallback(() => {
    if (map.current) return;
    let initialCenter = [4.896029, 52.371807];

    if (project?.center) {
      initialCenter = project.center;
      centerSetRef.current = true;
    } else if (project && !project.center) {
      initialCenter = [4.896029, 52.371807];
    }

    map.current = new maptilersdk.Map({
      container: mapContainer.current,
      style: MAP_STYLES_OPTIONS.Satellite,
      center: initialCenter,
      zoom: 14,
    });

    map.current.on('load', () => {
      setMapLoaded(true);
    });
  }, [project]);

  // Add map style control when map is loaded and active
  useEffect(() => {
    if (!active || !map.current || !mapLoaded || mapStyleControlRef.current) return;

    // Add control with a delay to ensure it's added after default controls
    const timer = setTimeout(() => {
      if (!map.current || mapStyleControlRef.current) return;

      const styleControl = new MapStyleControl({
        overlayRef: mapStyleOverlayRef,
        buttonRef: mapStyleControlButtonRef,
      });
      map.current.addControl(styleControl);
      mapStyleControlRef.current = styleControl;

      // Move the control to the bottom of the control stack
      const controlContainer = styleControl._container;
      if (controlContainer && controlContainer.parentNode) {
        controlContainer.parentNode.appendChild(controlContainer);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (map.current && mapStyleControlRef.current) {
        try {
          map.current.removeControl(mapStyleControlRef.current);
        } catch (e) {
          // Control might already be removed
          console.warn('Error removing map style control:', e);
        }
        mapStyleControlRef.current = null;
      }
    };
  }, [active, mapLoaded]);

  const changeMapStyle = useCallback((styleValue) => {
    if (!map.current) return;
    setMapStyle(styleValue);
    map.current.setStyle(styleValue);
    if (mapStyleOverlayRef.current) {
      mapStyleOverlayRef.current.hide();
    }
  }, []);

  useEffect(() => {
    if (!project || !map.current || !mapLoaded) return;

    const hasUrlParams = urlSearch.get('lng') && urlSearch.get('lat');
    const hasSavedPosition = window.localStorage.getItem(`ProjectMap:${project.id}`);

    if (hasUrlParams || hasSavedPosition) {
      return;
    }

    if (project.center) {
      const currentCenter = map.current.getCenter();
      const centerDiff =
        Math.abs(currentCenter.lng - project.center[0]) +
        Math.abs(currentCenter.lat - project.center[1]);

      if (centerDiff > 0.1) {
        const isFirstSet = !centerSetRef.current;

        if (isFirstSet) {
          requestAnimationFrame(() => {
            if (map.current) {
              map.current.setCenter(project.center);
              map.current.setZoom(14);
            }
          });
          centerSetRef.current = true;
        } else {
          map.current.flyTo({
            center: project.center,
            zoom: 14,
            duration: 1000,
          });
        }
      } else {
        centerSetRef.current = true;
      }
    }
  }, [project, project?.center, mapLoaded, urlSearch, project?.id]);

  useEffect(() => {
    if (!project || !map.current || !mapLoaded) return;

    const hasUrlParams = urlSearch.get('lng') && urlSearch.get('lat');
    const hasSavedPosition = window.localStorage.getItem(`ProjectMap:${project.id}`);

    if (hasUrlParams || hasSavedPosition || project.center) return;

    const timeoutId = setTimeout(() => {
      if (map.current && project && !project.center) {
        map.current.centerOnIpPoint(zoom);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [project, project?.center, mapLoaded, urlSearch, zoom]);

  const loadMap = useCallback(() => {
    if (!project || !map.current || !mapLoaded) return;

    map.current.off('drag');

    map.current.on('drag', () => {
      const { lng, lat } = map.current.getCenter();
      const z = map.current.getZoom();
      window.localStorage.setItem(`ProjectMap:${project.id}`, `${z}:${lng}:${lat}`);
    });

    if (urlSearch.get('lng') && urlSearch.get('lat')) {
      map.current.setCenter([+urlSearch.get('lng'), +urlSearch.get('lat')]);
      map.current.setZoom(20);
    } else {
      const saved = window.localStorage.getItem(`ProjectMap:${project.id}`);
      if (saved) {
        const [z, lng, lat] = saved.split(':').map(Number);
        map.current.setCenter([lng, lat]);
        map.current.setZoom(z);
      }
    }
  }, [project, urlSearch, zoom, mapLoaded]);

  const fetchLayers = useCallback(() => {
    if (!project || !project.drawings) return;
    setAllLayers(project.drawings);
    setVisibleLayers(project.drawings);
  }, [project]);

  // Initialize geoJSON sources for Line layers
  const initObjects = useCallback(() => {
    if (!visibleLayers || skipMarkerRefresh.current) {
      skipMarkerRefresh.current = false;
      return;
    }

    visibleLayers.forEach((drawing) => {
      if (!['Layer', 'DPArea'].includes(drawing.type)) return;

      if (!map.current.getSource(drawing.name)) {
        map.current.addSource(drawing.name, {
          type: 'geojson',
          data: drawing.geoJSON,
        });
      }
    });
  }, [visibleLayers]);

  const enlargeMarker = (asset, scale = 1.3) => {
    const element = document.getElementById(`${asset.id}-marker`);
    if (!element) return;
    const svg = element.querySelector('svg');
    const label = element.querySelector(`[id="${asset.id}-label"]`);

    // Scale SVG
    if (svg) {
      svg.style.transition = 'transform 300ms ease-in-out';
      svg.style.transformOrigin = 'center';
      svg.style.transform = `scale(${scale})`;
    }

    // Scale label but preserve the translateX(-50%) centering
    if (label) {
      label.style.transition = 'transform 300ms ease-in-out';
      label.style.transformOrigin = 'center';
      label.style.transform = `translateX(-50%) scale(${scale})`;
    }
  };

  const resetMarker = (asset) => {
    const element = document.getElementById(`${asset.id}-marker`);
    if (!element) return;
    const svg = element.querySelector('svg');
    const label = element.querySelector(`[id="${asset.id}-label"]`);

    // Reset SVG
    if (svg) {
      svg.style.transition = 'transform 300ms ease-in-out';
      svg.style.transformOrigin = 'center';
      svg.style.transform = 'scale(1)';
    }

    // Reset label but preserve the translateX(-50%) centering
    if (label) {
      label.style.transition = 'transform 300ms ease-in-out';
      label.style.transformOrigin = 'center';
      label.style.transform = 'translateX(-50%) scale(1)';
    }
  };

  const flashMarker = (asset) => {
    const element = document.getElementById(`${asset.id}-marker`);
    if (!element) return;
    const svg = element.querySelector('svg');
    if (!svg) return;
    const el = svg.querySelectorAll('g');
    if (!el || el.length === 0) return;
    const isLastModified = asset.modified_at === lastModifiedAsset?.modified_at;
    el.forEach((g) => {
      g.style.transition = `fill 100ms ease-in-out`;
      g.setAttribute('fill', FLASH_COLOR);
      setTimeout(() => {
        g.setAttribute('fill', isLastModified ? LAST_MODIFIED_COLOR : ASSET_COLOR);
      }, 300);
    });
  };

  const processingClickRef = useRef(false);

  const lastMessageTimestamp = useRef(null);
  const lastMessage = useRef(null);
  const displayMessage = (message, type = 'error') => {
    const timestamp = Date.now();
    if (lastMessage.current === message && timestamp - lastMessageTimestamp.current < 1000) {
      return;
    }
    messageApi.open({
      type,
      content: message,
      style: {
        zIndex: 1500,
      },
      top: isMobile ? 100 : 50,
      duration: 5,
    });
    lastMessageTimestamp.current = timestamp;
    lastMessage.current = message;
    setTimeout(() => {
      lastMessageTimestamp.current = null;
      lastMessage.current = null;
    }, 1000);
  };

  const handleAssetClickRef = useRef();

  const handleAssetClick = (asset) => {
    if (processingClickRef.current) {
      return;
    }

    const isLinking = linkingAssetsRef.current;

    if (!isLinking) {
      setAssetsToConnect([]);
      handleAssetClickNotLinking(asset);
      return;
    }

    handleAssetClickLinking(asset);
  };

  useEffect(() => {
    handleAssetClickRef.current = handleAssetClick;
  });

  const assetsToConnectRef = useRef([]);
  const visibleAssetsRef = useRef([]);

  useEffect(() => {
    assetsToConnectRef.current = assetsToConnect;

    return () => {
      assetsToConnectRef.current = [];
    };
  }, [assetsToConnect]);

  useEffect(() => {
    visibleAssetsRef.current = visibleAssets;

    return () => {
      visibleAssetsRef.current = [];
    };
  }, [visibleAssets]);

  const validateAssetSelection = useCallback((asset, assetsToConnect) => {
    if (assetsToConnect.includes(asset)) {
      return { valid: false, error: 'Asset already selected', reset: false, flash: true };
    }

    return { valid: true, reset: false, flash: false };
  }, []);

  const handleAssetClickLinking = (asset) => {
    processingClickRef.current = true;

    const validation = validateAssetSelection(
      asset,
      assetsToConnectRef.current,
      visibleAssetsRef.current
    );

    if (!validation.valid) {
      displayMessage(validation.error);
      if (validation.reset) {
        resetMarker(asset);
      }
      if (validation.flash) {
        flashMarker(asset);
      }
      processingClickRef.current = false;
      return;
    }

    setAssetsToConnect((currentAssets) => [...currentAssets, asset]);
    enlargeMarker(asset);

    setTimeout(() => {
      processingClickRef.current = false;
    }, 100);
  };

  const handleAssetClickNotLinking = (asset) => {
    assetViewOpen.current = true;
    setIsAssetViewOpen(true);

    setAssetViewAsset({
      ...asset,
      layerName: allLayers.find((l) => l.id === asset.drawing_id)?.name ?? 'Not specified',
    });
  };

  const handleConnectionFinished = () => {
    connectAssets();
  };

  const handleAssetUpdated = (asset) => {
    if (assetsFromApi.find((a) => a.id === asset.id)) {
      setAssets((cur) => cur.map((a) => (a.id === asset.id ? asset : a)));
      setVisibleAssets((cur) => cur.map((a) => (a.id === asset.id ? asset : a)));
      return;
    }

    // if the asset is not in the assetsFromApi, it means it's a new asset
    setAssets((cur) => [...cur, asset]);
    setVisibleAssets((cur) => [...cur, asset]);
  };

  // Helper function to create edges between assets
  // Creates edges between consecutive pairs in the selection order
  // This preserves the order in which assets were selected
  const createEdgesBetweenAssets = async (surveyId, assets) => {
    if (assets.length < 2) return;

    // Create edges between consecutive pairs: [A, B, C] → A→B, B→C
    // IMPORTANT: Preserve the exact order assets were selected
    const edgePromises = [];
    for (let i = 0; i < assets.length - 1; i++) {
      const assetA = assets[i];
      const assetB = assets[i + 1];

      edgePromises.push(
        postAssetEdge({
          projectId,
          surveyId,
          edge: {
            asset_a_id: assetA.id,
            asset_b_id: assetB.id,
            directed: false,
            relation_type: 'connection',
          },
        }).catch((err) => {
          console.error(`Failed to create edge between ${assetA.id} and ${assetB.id}:`, err);
          // Don't throw - continue creating other edges even if one fails
          return null;
        })
      );
    }

    await Promise.all(edgePromises);
  };

  const updateExistingSurvey = (surveyId, newlySelectedAssets) => {
    // Get existing survey members to check which assets are already in the survey
    const existingSurvey = surveysFromApi.find((s) => s.id === surveyId);
    const existingAssetIds = existingSurvey?.members
      ? new Set(existingSurvey.members.map((m) => m.asset_id))
      : new Set();

    // Filter to only newly selected assets (not already in survey)
    const newAssets = newlySelectedAssets.filter((a) => !existingAssetIds.has(a.id));
    const allAssetsToAdd = [...newlySelectedAssets]; // Add all selected assets to survey members

    const newSurveyMembersPayload = allAssetsToAdd.map((asset) => {
      return {
        asset_id: asset.id,
        seq_index: asset.seq_index,
      };
    });

    createSurveyMemberBatch({
      projectId: projectId,
      surveyId: surveyId,
      members: newSurveyMembersPayload,
    })
      .then(async (response) => {
        const { route_geojson, route_length_m, survey_id } = response.data;
        if (!route_geojson || !route_length_m || !survey_id) {
          displayMessage('Failed to update survey', 'error');
          return;
        }

        // IMPORTANT: Only create edges between the newly selected assets in the order they were selected
        // This prevents creating unwanted edges between assets that are already connected
        if (newlySelectedAssets.length >= 2) {
          await createEdgesBetweenAssets(surveyId, newlySelectedAssets);
        }

        displayMessage('Survey updated successfully', 'success');

        // Refetch surveys to get updated member data with seq_index and edges
        // IMPORTANT: Don't update local state until refetch completes to avoid stale data
        refetchSurveys().then((result) => {
          // Force update surveys state with fresh data from refetch
          if (result.data) {
            setSurveys(result.data);
          }
          // Now that we have fresh data, reload the visualization
          setTimeout(() => {
            if (map.current && visibleAssets.length > 0) {
              if (loadAssetsRef.current) loadAssetsRef.current();
              if (loadSurveysLinesRef.current) loadSurveysLinesRef.current();
            }
          }, 100);
        });
      })
      .catch((err) => {
        console.error('Error updating survey:', err);
        displayMessage('Failed to update survey', 'error');
      });
  };

  const createNewSurvey = (assets) => {
    const newSurveyPayload = {
      projectId: projectId,
      lat: assets[0].latitude,
      lng: assets[0].longitude,
      alt: assets[0]?.altitude,
      uuid: uuidv4(),
    };

    createSurvey(newSurveyPayload)
      .then(async (response) => {
        const surveyId = response.data.id;
        let seqIndex = 0; // 1 is the first asset
        const newSurveyMembersPayload = assets.map((asset) => {
          seqIndex++;
          return {
            asset_id: asset.id,
            seq_index: seqIndex,
          };
        });

        const memberResponse = await createSurveyMemberBatch({
          projectId: projectId,
          surveyId: surveyId,
          members: newSurveyMembersPayload,
        });

        const { route_geojson, route_length_m, survey_id } = memberResponse.data;
        if (!route_geojson || !route_length_m || !survey_id) {
          displayMessage('Failed to create survey', 'error');
          return;
        }

        // Create edges between the selected assets
        await createEdgesBetweenAssets(surveyId, assets);

        displayMessage('Survey created successfully', 'success');

        // Refetch surveys to get updated member data with seq_index and edges
        // IMPORTANT: Don't update local state until refetch completes to avoid stale data
        refetchSurveys().then((result) => {
          // Force update surveys state with fresh data from refetch
          if (result.data) {
            setSurveys(result.data);
          }
          setTimeout(() => {
            if (map.current && visibleAssets.length > 0) {
              if (loadAssetsRef.current) loadAssetsRef.current();
              if (loadSurveysLinesRef.current) loadSurveysLinesRef.current();
            }
          }, 100);
        });
      })
      .catch((err) => {
        console.error('Error creating survey:', err);
        displayMessage('Failed to create survey', 'error');
      });
  };

  // Connect two assets
  const connectAssets = () => {
    const newAssetsToConnect = assetsToConnect;
    if (!newAssetsToConnect.length) return;

    // find all surveys that have any of the selected assets
    const involvedSurveys = surveysFromApi.filter((survey) =>
      survey.members.some((m) => newAssetsToConnect.some((a) => a.id === m.asset_id))
    );

    if (involvedSurveys.length === 0) {
      // create a new survey
      createNewSurvey(newAssetsToConnect);
    } else if (involvedSurveys.length === 1) {
      // update that survey
      // IMPORTANT: Pass assets in the exact order they were selected
      const surveyId = involvedSurveys[0].id;
      updateExistingSurvey(surveyId, newAssetsToConnect);
    } else {
      // multiple surveys involved -> merge them into a single survey
      const surveyIds = involvedSurveys.map((s) => s.id);
      // pick a target - e.g. keep the earliest created (or prompt user)
      const targetSurveyId = involvedSurveys.reduce(
        (best, s) => (s.created_at < best.created_at ? s : best),
        involvedSurveys[0]
      ).id;

      mergeSurveys({ projectId, surveyIds, targetSurveyId, newAssetsToConnect })
        .then(async (response) => {
          if (response.error) {
            displayMessage('Failed to merge surveys', 'error');
            return;
          }
          const { survey_id, merged_from, route_geojson, route_length_m } = response.data;

          // Create edges between the newly connected assets
          await createEdgesBetweenAssets(survey_id, newAssetsToConnect);

          displayMessage('Surveys merged successfully', 'success');

          // Refetch surveys to get updated member data with seq_index and edges
          // IMPORTANT: Don't update local state until refetch completes to avoid stale data
          refetchSurveys().then((result) => {
            // Force update surveys state with fresh data from refetch
            if (result.data) {
              setSurveys(result.data);
            }
            setTimeout(() => {
              if (map.current && visibleAssets.length > 0) {
                if (loadAssetsRef.current) loadAssetsRef.current();
                if (loadSurveysLinesRef.current) loadSurveysLinesRef.current();
              }
            }, 100);
          });
        })
        .catch((err) => {
          console.error(err);
          displayMessage('Merge failed', 'error');
        });
    }

    // cleanup UI
    setAssetsToConnect([]);
    linkingAssetsRef.current = false;
    visibleAssets.forEach((asset) => resetMarker(asset));
  };

  function lastModified(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return null;

    const parseMs = (s) => {
      if (!s) return NaN;
      const ms = Date.parse(s);
      return isNaN(ms) ? NaN : ms;
    };

    let best = null;
    let bestMs = -Infinity;

    for (const obj of arr) {
      const ms = parseMs(obj.modified_at);
      if (!isNaN(ms) && ms > bestMs) {
        bestMs = ms;
        best = obj;
      }
    }

    return best;
  }

  // Create a memoized map of assetId -> surveyIndex for O(1) lookups
  const assetSurveyIndexMap = useMemo(() => {
    const surveysToCheck = surveysFromApi || surveys;
    if (!surveysToCheck || !Array.isArray(surveysToCheck)) return new Map();

    const indexMap = new Map();
    surveysToCheck.forEach((survey) => {
      if (survey.members && Array.isArray(survey.members)) {
        survey.members.forEach((member) => {
          if (member.asset_id && member.seq_index !== undefined && member.seq_index !== null) {
            indexMap.set(member.asset_id, member.seq_index);
          }
        });
      }
    });
    return indexMap;
  }, [surveysFromApi, surveys]);

  // Load asset markers
  const loadAssets = useCallback(() => {
    if (!visibleAssets || !Array.isArray(visibleAssets)) return;

    // Clear existing markers
    assetMarkers.current.forEach((marker) => marker.remove());
    assetMarkers.current = [];

    // Create new markers
    visibleAssets.forEach((asset) => {
      const marker = new maptilersdk.Marker({ color: ASSET_COLOR })
        .setLngLat([asset.longitude, asset.latitude])
        .addTo(map.current);

      const element = marker._element;
      element.onmouseenter = (e) => (e.target.style.cursor = 'pointer');
      element.id = `${asset.id}-marker`;

      // Find survey index for this asset (O(1) lookup)
      const surveyIndex = assetSurveyIndexMap.get(asset.id) || null;

      // Add label - centered under the marker
      const label = document.createElement('div');
      Object.assign(label.style, {
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: '4px',
        zIndex: '9999',
        whiteSpace: 'nowrap',
        color: 'white',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        pointerEvents: 'none',
        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
      });

      // Build label text with survey index if available
      let labelText = asset.name || '';
      if (surveyIndex !== null) {
        labelText += ` (${surveyIndex})`;
      }
      label.textContent = labelText;
      label.id = `${asset.id}-label`;
      element.appendChild(label);

      element.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (handleAssetClickRef.current) {
          handleAssetClickRef.current(asset);
        }
      };

      assetMarkers.current.push(marker);
    });

    const newLastModifiedAsset = lastModified(visibleAssets);
    // Only update if the value actually changed to prevent unnecessary re-renders
    if (newLastModifiedAsset) {
      setLastModifiedAsset((prev) => {
        if (prev?.id === newLastModifiedAsset?.id) return prev;
        return newLastModifiedAsset;
      });
    }
  }, [visibleAssets, assetSurveyIndexMap]);

  // Update refs for loadAssets
  useEffect(() => {
    loadAssetsRef.current = loadAssets;
  }, [loadAssets]);

  useEffect(() => {
    if (lastModifiedAsset) {
      setTimeout(() => {
        handleLastModifiedAsset(lastModifiedAsset);
      }, 200);
    }
  }, [lastModifiedAsset]);

  const handleLastModifiedAsset = (asset) => {
    resetLastModifiedAsset();
    const element = document.getElementById(`${asset.id}-marker`);
    element.classList.add('last-modified-asset');
    if (!element) return;
    const svg = element.querySelector('svg');
    if (!svg) return;

    const g = svg.querySelectorAll('g');
    if (!g || g.length === 0) return;

    g.forEach((g) => {
      g.style.transition = 'fill 200ms ease-in-out';
      g.setAttribute('fill', LAST_MODIFIED_COLOR);
    });
  };

  const colorAsset = (asset, color) => {
    const element = document.getElementById(`${asset.id}-marker`);
    if (!element) return;
    const svg = element.querySelector('svg');
    if (!svg) return;
    const g = svg.querySelectorAll('g');
    if (!g || g.length === 0) return;

    g.forEach((g) => {
      g.style.transition = 'fill 200ms ease-in-out';
      g.setAttribute('fill', color);
    });
  };

  const resetAllAssetsColor = () => {
    const lastModifiedAsset = lastModified(visibleAssets);
    visibleAssets.forEach((asset) => {
      colorAsset(asset, ASSET_COLOR);
      if (lastModifiedAsset && asset.id === lastModifiedAsset.id) {
        colorAsset(asset, LAST_MODIFIED_COLOR);
      }
    });
  };

  const resetLastModifiedAsset = () => {
    const element = document.querySelector('.last-modified-asset');
    if (!element) return;
    const svg = element.querySelector('svg');
    if (!svg) return;
    const g = svg.querySelectorAll('g');
    if (!g || g.length === 0) return;

    g.forEach((g) => {
      g.style.transition = 'fill 200ms ease-in-out';
      g.setAttribute('fill', ASSET_COLOR);
    });
  };

  const surveysFromApiRef = useRef();

  useEffect(() => {
    if (!surveysFromApi || !Array.isArray(surveysFromApi)) return;

    const prevSurveys = surveysFromApiRef.current;
    // Quick reference check firstorders/189/47/technology
    if (prevSurveys === surveysFromApi) return;

    // Store the reference
    surveysFromApiRef.current = surveysFromApi;

    // Only update state if content actually changed (compare by length and IDs)
    setSurveys((prev) => {
      if (prev === surveysFromApi) return prev;
      if (!prev || !Array.isArray(prev) || prev.length !== surveysFromApi.length) {
        return surveysFromApi;
      }
      // Check if any IDs are different
      const idsChanged = prev.some((survey, idx) => survey?.id !== surveysFromApi[idx]?.id);
      if (idsChanged) return surveysFromApi;
      return prev;
    });
  }, [surveysFromApi]);

  // Load asset connection lines
  const loadSurveysLines = useCallback(() => {
    const parsedSurveys = surveys.map((survey) => {
      // Build GeoJSON from edges if available, otherwise use route_geojson
      let geoJSON = null;

      if (survey.edges && Array.isArray(survey.edges) && survey.edges.length > 0) {
        // Create GeoJSON from edges
        // First, build a map of asset_id -> location from survey members
        const assetLocationMap = new Map();
        if (survey.members && Array.isArray(survey.members)) {
          survey.members.forEach((member) => {
            if (member.longitude !== null && member.latitude !== null) {
              assetLocationMap.set(member.asset_id, [member.longitude, member.latitude]);
            }
          });
        }

        // Create LineString features for each edge
        const features = survey.edges
          .map((edge) => {
            const locA = assetLocationMap.get(edge.asset_a_id);
            const locB = assetLocationMap.get(edge.asset_b_id);

            if (!locA || !locB) {
              // Skip edges where we don't have location data
              return null;
            }

            return {
              type: 'Feature',
              geometry: {
                type: 'LineString',
                coordinates: [locA, locB],
              },
              properties: {
                edge_id: edge.id,
                asset_a_id: edge.asset_a_id,
                asset_b_id: edge.asset_b_id,
                directed: edge.directed,
              },
            };
          })
          .filter((f) => f !== null);

        if (features.length > 0) {
          geoJSON = {
            type: 'FeatureCollection',
            features: features,
          };
        }
      }

      // Fall back to route_geojson if no edges or edge-based GeoJSON couldn't be built
      if (!geoJSON) {
        geoJSON =
          typeof survey.route_geojson === 'string'
            ? JSON.parse(survey.route_geojson)
            : survey.route_geojson;
      }

      return {
        id: survey.id,
        geoJSON,
        length: survey.route_length_m,
        uuid: survey.uuid,
      };
    });

    parsedSurveys.forEach((survey) => {
      if (!survey.geoJSON) return;

      const layerName = `survey-${survey.id}-${survey.uuid}-line`;
      if (map.current.getLayer(layerName)) {
        map.current.removeLayer(layerName);
      }
      if (map.current.getSource(layerName)) {
        map.current.removeSource(layerName);
      }

      const searchSurveyId = Number(searchedSurveyId);

      map.current.addSource(layerName, {
        type: 'geojson',
        data: survey.geoJSON,
      });

      map.current.addLayer({
        id: layerName,
        type: 'line',
        source: layerName,
        paint: {
          'line-color': searchSurveyId === survey.id ? HIGHLIGHTED_COLOR : ASSET_COLOR,
          'line-width': 2,
        },
      });
    });
  }, [surveys, searchedSurveyId]);

  // Update ref for loadSurveysLines after it's defined
  useEffect(() => {
    loadSurveysLinesRef.current = loadSurveysLines;
  }, [loadSurveysLines]);

  useEffect(() => {
    fetchLayers();
    // Cleanup function removed - shouldn't reset state in cleanup
  }, [project, fetchLayers]);

  const initializedRef = useRef(false);
  useEffect(() => {
    if (layersOptions.length > 0 && !initializedRef.current) {
      setSelectedLayers(layersOptions);
      initializedRef.current = true;
    }
  }, [layersOptions]);

  // Load drawing layers
  const loadLayers = useCallback(() => {
    // Remove layers that shouldn't be visible
    allLayers.forEach((drawing) => {
      if (drawing.type !== 'Layer') return;

      const shouldBeVisible = visibleLayers.some((vl) => vl.id === drawing.id);
      const layer = map.current.getLayer(drawing.name);

      if (layer && !shouldBeVisible) {
        map.current.removeLayer(drawing.name);
      }
    });

    // Add layers that should be visible
    visibleLayers.forEach((drawing) => {
      if (drawing.type !== 'Layer') return;
      const layer = map.current.getLayer(drawing.name);
      if (!layer) {
        map.current.addLayer({
          id: drawing.name,
          type: 'line',
          source: drawing.name,
          paint: {
            'line-color': drawing.color,
          },
        });
      }
    });
  }, [visibleLayers, allLayers]);

  // Load distribution points
  const loadDP = useCallback(() => {
    distributionPointsMarkers.forEach((dp) => dp.remove());

    // Remove layers that shouldn't be visible
    allLayers.forEach((drawing) => {
      if (drawing.type !== 'DP') return;
      const layer = map.current.getLayer(drawing.name);
      if (layer && !visibleLayers.some((vl) => vl.id === drawing.id)) {
        map.current.removeLayer(drawing.name);
      }
    });

    visibleLayers.forEach((drawing) => {
      if (drawing.type != 'DP') return;

      drawing.geoJSON.features.forEach((dp) => {
        if (!dp.geometry.coordinates) return;
        if (dp.geometry.type !== 'Point') return;
        const [lng, lat] = dp.geometry.coordinates;
        const marker = new maptilersdk.Marker({
          color: drawing.color,
        })
          .setLngLat([lng, lat])
          .addTo(map.current);

        marker._element.onmouseenter = (e) => (e.target.style.cursor = 'pointer');
        marker._element.onclick = () => console.log('click');

        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style['white-space'] = 'nowrap';
        div.appendChild(document.createTextNode(dp.properties.Label));

        marker._element.appendChild(div);

        distributionPointsMarkers.push(marker);
      });
    });

    setDistributionPointsMarkers(distributionPointsMarkers);
  }, [visibleLayers]);

  // Load distribution point areas
  const loadDPArea = useCallback(() => {
    // Remove layers that shouldn't be visible
    allLayers.forEach((drawing) => {
      if (drawing.type !== 'DPArea') return;
      const layer = map.current.getLayer(drawing.name);
      if (layer && !visibleLayers.some((vl) => vl.id === drawing.id)) {
        map.current.removeLayer(drawing.name);
      }
    });

    visibleLayers.forEach((drawing) => {
      if (drawing.type != 'DPArea') return;

      const layer = map.current.getLayer(drawing.name);
      if (layer) {
        if (!drawingsSelectedRef.current || !drawingsSelectedRef.current.includes(drawing.key)) {
          map.current.removeLayer(drawing.name);
        }
        return;
      }

      map.current.addLayer({
        id: drawing.name,
        type: 'line',
        source: drawing.name,
        paint: {
          'line-color': drawing.color,
          'line-width': 20,
        },
      });
    });
  }, [visibleLayers]);

  // Menu handlers
  const handleMenuClick = (event) => {
    setMenuOpen(!menuOpen);
    menuRef.current.toggle(event);
  };

  const handleAddAssetMenuClick = (event) => {
    resetAllAssetsColor();
    displayMessage("Select assets to connect, then click 'Stop Connecting' to finish", 'info');
    setMenuOpen(false);
    menuRef.current.toggle(event);
    linkingAssetsRef.current = true;
    setAssetsToConnect([]);
    visibleAssets.forEach((asset) => resetMarker(asset));
    // Clear any highlighted survey so lines go back to blue (useEffect will redraw)
    setSearchedSurveyId(null);
  };

  const startRecordingMenuClick = (event) => {
    setMenuOpen(false);
    menuRef.current.toggle(event);
    linkingAssetsRef.current = false;
    setAssetsToConnect([]);
    visibleAssets.forEach((asset) => resetMarker(asset));
    openCamera();
  };

  const cancelConnectionMenuClick = (event) => {
    setMenuOpen(false);
    menuRef.current.toggle(event);
    linkingAssetsRef.current = false;
    setAssetsToConnect([]);
    assetsToConnectRef.current = [];
    visibleAssets.forEach((asset) => resetMarker(asset));
  };

  // Menu items
  const menuItems = [
    {
      id: 'connect-assets-button',
      label: linkingAssetsRef.current ? 'Stop Connecting' : 'Connect Assets',
      visible: true,
      command: linkingAssetsRef.current ? handleConnectionFinished : handleAddAssetMenuClick,
    },
    {
      id: 'cancel-connection-button',
      label: 'Cancel Connection',
      visible: linkingAssetsRef.current,
      command: cancelConnectionMenuClick,
    },
    {
      id: 'start-recording-button',
      label: 'Start Recording',
      visible: isMobile,
      command: startRecordingMenuClick,
    },
  ];

  // Effects
  const assetsFromApiRef = useRef();

  useEffect(() => {
    if (!assetsFromApi || !Array.isArray(assetsFromApi)) return;

    const prevAssets = assetsFromApiRef.current;
    // Quick reference check first
    if (prevAssets === assetsFromApi) return;

    // Store the reference
    assetsFromApiRef.current = assetsFromApi;

    // Only update state if content actually changed (compare by length and IDs)
    const updateIfChanged = (prev) => {
      if (prev === assetsFromApi) return prev;
      if (!prev || !Array.isArray(prev) || prev.length !== assetsFromApi.length) {
        return assetsFromApi;
      }
      // Check if any IDs are different
      const idsChanged = prev.some((asset, idx) => asset?.id !== assetsFromApi[idx]?.id);
      if (idsChanged) return assetsFromApi;
      return prev;
    };

    setAssets(updateIfChanged);
    setVisibleAssets(updateIfChanged);
  }, [assetsFromApi]);

  useEffect(() => {
    if (active && mapContainer.current) {
      initMap();
    }

    return () => {
      if (map.current) {
        map.current.off('drag');
      }
    };
  }, [active, initMap]);

  useEffect(() => {
    if (active && map.current && mapLoaded && project) {
      loadMap();
    }

    return () => {
      if (map.current) {
        map.current.off('drag');
      }
    };
  }, [project, active, mapLoaded, loadMap]);

  useEffect(() => {
    if (active && map.current && mapLoaded && project) {
      setTimeout(() => {
        loadAssets();
      }, 50);
    }
  }, [active, mapLoaded, project, loadAssets]);

  useEffect(() => {
    if (active && map.current && mapLoaded && project && surveys.length > 0) {
      setTimeout(() => {
        if (loadSurveysLinesRef.current) {
          loadSurveysLinesRef.current();
        }
      }, 50);
    }
  }, [active, mapLoaded, project, surveys, searchedSurveyId]);

  // Load drawing layers (Lines, DP, DPArea)
  useEffect(() => {
    if (active && map.current && mapLoaded && visibleLayers.length > 0) {
      initObjects();
      setTimeout(() => {
        loadLayers();
        loadDP();
        loadDPArea();
      }, 50);
    }
  }, [active, mapLoaded, visibleLayers, initObjects, loadLayers, loadDP, loadDPArea]);

  // Handle tab switching with proper resize
  useEffect(() => {
    if (active && map.current && mapLoaded) {
      const timer = setTimeout(() => {
        map.current.resize();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [active, mapLoaded]);

  // Move buildAssetOptions before the useEffect and memoize it
  const buildAssetOptions = useCallback(
    (assets) => {
      const newOptions = [];

      assets.forEach((asset) => {
        const linkedLayer = allLayers.find((layer) => layer.id === asset.drawing_id);

        const presentId = newOptions.findIndex((option) => option.label === asset.name);

        const option = {
          label: asset.name,
          value: [asset.id],
          layerColor: linkedLayer?.color,
        };

        if (presentId === -1) {
          newOptions.push(option);
        } else {
          newOptions[presentId].value.push(asset.id);
        }
      });

      return newOptions;
    },
    [allLayers]
  );

  useEffect(() => {
    setAssetsOptions(buildAssetOptions(visibleAssets));
  }, [visibleAssets, buildAssetOptions]);

  const handleLayerSelect = (e) => {
    const selectedLayers = e.value;
    setSelectedLayers(selectedLayers);

    if (!selectedLayers.length) {
      setVisibleLayers([]);
      setVisibleAssets(assets);
      return;
    }

    const visibleLayers = allLayers.filter((layer) =>
      selectedLayers.map((layer) => layer.id).includes(layer.id)
    );

    const selectedLayerIds = visibleLayers.map((layer) => layer.id);

    const visibleAssets = assets.filter(
      (asset) => asset.drawing_id === null || selectedLayerIds.includes(asset.drawing_id)
    );

    setVisibleLayers(visibleLayers);
    setVisibleAssets(visibleAssets);
  };

  function getGeographicMidpoint(coords) {
    if (!coords.length) throw new Error('Coordinate list is empty');

    let x = 0,
      y = 0,
      z = 0;

    for (const [lon, lat] of coords) {
      const latRad = (lat * Math.PI) / 180;
      const lonRad = (lon * Math.PI) / 180;

      x += Math.cos(latRad) * Math.cos(lonRad);
      y += Math.cos(latRad) * Math.sin(lonRad);
      z += Math.sin(latRad);
    }

    const total = coords.length;
    x /= total;
    y /= total;
    z /= total;

    const lonMid = Math.atan2(y, x);
    const hyp = Math.sqrt(x * x + y * y);
    const latMid = Math.atan2(z, hyp);

    return [(lonMid * 180) / Math.PI, (latMid * 180) / Math.PI];
  }

  const handleAssetSelect = useCallback(
    (e) => {
      resetAllAssetsColor();

      const assetIds = e.value;

      if (!assetIds.length) {
        setSelectedAssets([]);
        return;
      }

      setSelectedAssets(assetIds);

      const value = assetIds.flat();

      const assetsList = visibleAssets.filter((asset) => value.includes(asset.id));

      const coordinatesList = assetsList.map((asset) => [asset.longitude, asset.latitude]);
      const center = getGeographicMidpoint(coordinatesList);

      assetsList.forEach((asset) => {
        colorAsset(asset, HIGHLIGHTED_COLOR);
      });

      const bounds = calculateBounds(coordinatesList);

      map.current.fitBounds(bounds, {
        padding: { top: 100, bottom: 100, left: 100, right: 100 },
        maxZoom: 40,
        duration: 1000,
      });
    },
    [visibleAssets, map.current]
  );

  const assetMenuRef = useRef(null);
  const layerMenuRef = useRef(null);

  const assetOptionTemplate = (option) => {
    if (!option) return;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '15px',
            height: '15px',
            borderRadius: '2px',
            backgroundColor: option.layerColor,
            display: 'inline-block',
          }}
        />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 10px)',
          }}
        >
          {option.label}
        </span>
      </div>
    );
  };

  const layerOptionTemplate = (option) => {
    if (!option) return;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '15px',
            height: '15px',
            borderRadius: '2px',
            backgroundColor: option.color,
            display: 'inline-block',
          }}
        />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: 'calc(100% - 10px)',
          }}
        >
          {option.label}
        </span>
      </div>
    );
  };

  return (
    <div
      id="project-map"
      style={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        id="dropdown-container"
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          flexShrink: 0,
          padding: '10px 14px',
          gap: isMobile ? 12 : 16,
        }}
      >
        <MultiSelect
          options={assetsOptions}
          value={selectedAssets}
          onChange={handleAssetSelect}
          ref={assetMenuRef}
          id="asset-menu"
          filter
          showClear
          loading={assetsLoading}
          style={{ width: isMobile ? '100%' : '300px' }}
          placeholder="Search Assets..."
          itemTemplate={assetOptionTemplate}
        />
        <MultiSelect
          options={layersOptions}
          value={selectedLayers}
          onChange={handleLayerSelect}
          ref={layerMenuRef}
          loading={projectLoading}
          style={{ width: isMobile ? '100%' : '300px' }}
          id="layer-menu"
          placeholder="Layer"
          itemTemplate={layerOptionTemplate}
          showClear
          filter
        />
      </div>
      <div
        id="map-container"
        style={{
          flex: 1,
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {contextHolder}
        {/* Crosshair */}
        <div
          id="crosshair"
          style={{
            position: 'absolute',
            zIndex: 1000,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
          }}
        >
          <i
            className="pi pi-plus"
            style={{
              fontSize: 35,
            }}
          />
        </div>
        <Flex
          ref={mapContainer}
          style={{
            height: '100%',
            width: '100%',
            height: isMobile ? 'calc(100vh - 180px)' : '100%',
          }}
        />
        {/* Map Style Selector Overlay */}
        <OverlayPanel
          ref={mapStyleOverlayRef}
          style={{ padding: '8px' }}
          showCloseIcon={false}
          className="map-style-overlay"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {Object.entries(MAP_STYLES_OPTIONS).map(([key, value]) => (
              <button
                key={value}
                onClick={() => changeMapStyle(value)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: mapStyle === value ? '#1677ff' : '#f5f5f5',
                  color: mapStyle === value ? 'white' : '#333',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: mapStyle === value ? '600' : '400',
                  transition: 'all 0.2s ease',
                  minWidth: '120px',
                }}
                onMouseEnter={(e) => {
                  if (mapStyle !== value) {
                    e.target.style.background = '#e8e8e8';
                  }
                }}
                onMouseLeave={(e) => {
                  if (mapStyle !== value) {
                    e.target.style.background = '#f5f5f5';
                  }
                }}
              >
                {key}
              </button>
            ))}
          </div>
        </OverlayPanel>
        {/* Menu */}
        <div
          style={{
            position: 'absolute',
            bottom: isMobile ? 80 : 40,
            right: 20,
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Menu model={menuItems} popup ref={menuRef} id="map-menu" />
          <Button
            style={{
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 60,
              height: 60,
            }}
            onClick={handleMenuClick}
            aria-controls="map-menu"
            aria-haspopup
          >
            <i
              className="pi pi-plus"
              style={{
                fontSize: 30,
                transition: 'all 0.3s ease-in-out',
                rotate: menuOpen ? '45deg' : '0deg',
              }}
            />
          </Button>
        </div>
      </div>
      {/* Asset View */}
      {isAssetViewOpen && (
        <AssetView
          asset={assetViewAsset}
          allAssets={assets}
          isOpen={isAssetViewOpen}
          handleClose={() => {
            setIsAssetViewOpen(false);
            assetViewOpen.current = false;
          }}
          onAssetUpdated={handleAssetUpdated}
          ppkPosition={ppkPosition}
          ppkLoading={ppkLoading}
          locationType={locationType}
          messageContextHolder={contextHolder}
          locationTimedOut={locationTimedOut}
          requestPPKPosition={requestPPKPosition}
        />
      )}
    </div>
  );
}
